import classNames from 'classnames';
import React, { useLayoutEffect, useRef } from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useDrag } from '@use-gesture/react';
import Slot from '../../components/Slot';
import type { SpanValue } from '../formats/Span';
import * as styles from './CarouselSlider.module.scss';
import CarouselSliderButton from './CarouselSliderButton';
import type { ImageCarouselHeight } from './ImageCarouselHeight';
import { HEIGHTS } from './ImageCarouselHeight';
import ImageNode from './ImageNode';
import generateGetImageForDisplay from './generateGetImageForDisplay';
import { isUploadedCarouselItem } from './types';
import type { CarouselItem } from './types';

const ITEM_ATTRIBUTE_FOR_SCROLLING = 'data-slider-item-id';
const DRAG_SWIPE_THRESHOLD = 20;

interface CarouselSliderProps {
  fullscreen?: boolean;
  images: CarouselItem[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  height: ImageCarouselHeight;
  span: SpanValue | null;
  showControls: boolean;
}

const CarouselSlider = ({
  fullscreen,
  images,
  selectedIndex,
  height,
  span,
  onSelectedIndexChange,
  showControls,
}: CarouselSliderProps) => {
  const bind = useDrag((state) => {
    if (state.memo) return true;

    if (state.movement[0] > DRAG_SWIPE_THRESHOLD && selectedIndex > 0) {
      onSelectedIndexChange(selectedIndex - 1);
      return true;
    }
    if (
      state.movement[0] < -DRAG_SWIPE_THRESHOLD &&
      selectedIndex < images.length - 1
    ) {
      onSelectedIndexChange(selectedIndex + 1);
      return true;
    }

    return false;
  });

  const scrollingContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  let containerHeight: number = HEIGHTS[height];
  if (height === 'default') {
    containerHeight = Math.max(
      images.reduce<number>(
        (acc, image) => Math.min(acc, image.natural?.height ?? Infinity),
        HEIGHTS.medium,
      ),
      HEIGHTS.xsmall,
    );
  }

  const performScrolling = ({
    behavior,
  }: {
    behavior: 'smooth' | 'instant';
  }) => {
    const imageId = images[selectedIndex]?.id;
    if (!imageId) return;

    const container = containerRef.current;
    const scrollingContainer = scrollingContainerRef.current;
    if (!container || !scrollingContainer) return;

    const shouldScrollInstantly = behavior === 'instant';
    if (
      shouldScrollInstantly !==
      container.classList.contains(styles.instantScrolling)
    ) {
      container.classList.toggle(
        styles.instantScrolling,
        shouldScrollInstantly,
      );

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- force reflow to make the transition take effect
      container.scrollTop;
    }

    const item = container.querySelector<HTMLElement>(
      `[${ITEM_ATTRIBUTE_FOR_SCROLLING}="${imageId}"]`,
    );
    if (!item) return;

    const scollingContainerWidth = scrollingContainer.clientWidth;
    const containerWidth = container.clientWidth;
    const canShowAllImages = scollingContainerWidth >= containerWidth;

    if (canShowAllImages) {
      // If all images can be shown in the container without scrolling, center the selected image
      // while ensuring all images won't overflow the scrolling container.
      const preferredOffset =
        -item.offsetLeft + (scollingContainerWidth - item.clientWidth) / 2;

      const maxOffset = scollingContainerWidth - containerWidth;
      const offset = Math.min(Math.max(preferredOffset, 0), maxOffset);
      container.style.transform = `translateX(${offset}px)`;
    } else if (selectedIndex === 0) {
      container.style.transform = 'none';
    } else {
      const offset =
        selectedIndex === images.length - 1
          ? -item.offsetLeft + scollingContainerWidth - item.clientWidth
          : -item.offsetLeft + (scollingContainerWidth - item.clientWidth) / 2;
      container.style.transform = `translateX(${offset}px)`;
    }
  };

  const previousValuesRef = useRef<{
    images: typeof images;
    containerHeight: number;
    span: typeof span;
  } | null>(null);

  const onPerformScrollingEvent = useEffectEvent(performScrolling);
  useLayoutEffect(() => {
    // Perform smooth scrolling when only the selected index changes
    const shouldUseInstantScroll =
      previousValuesRef.current?.images !== images ||
      previousValuesRef.current?.containerHeight !== containerHeight ||
      previousValuesRef.current?.span !== span;

    // Update the ref with current values
    previousValuesRef.current = {
      images,
      containerHeight,
      span,
    };

    onPerformScrollingEvent({
      behavior: shouldUseInstantScroll ? 'instant' : 'smooth',
    });
  }, [selectedIndex, images, containerHeight, span]);

  const getImageForDisplay = generateGetImageForDisplay(images);

  return (
    <Slot {...bind()}>
      <div ref={scrollingContainerRef} className={styles.scrollingContainer}>
        <div
          ref={containerRef}
          className={styles.container}
          style={{ height: containerHeight }}
        >
          {images.map((image) => {
            const imageForDisplay = getImageForDisplay(image);

            // Calculate item widths to avoid layout changes when images are loaded
            const itemWidth = image.natural
              ? (containerHeight /
                  Math.max(image.natural.height, containerHeight)) *
                image.natural.width
              : null;

            // Hide the item if we don't have enough information to display it with the correct size.
            // This is to avoid layout shifts when images are loaded.
            const shouldHide =
              !isUploadedCarouselItem(imageForDisplay) &&
              !imageForDisplay.uploadPreviewSource &&
              !imageForDisplay.natural;

            return (
              <div
                key={image.id}
                className={classNames(styles.item, {
                  [styles.shouldHide]: shouldHide,
                })}
                role="tabpanel"
                aria-roledescription="slide"
                {...(shouldHide
                  ? {}
                  : { [ITEM_ATTRIBUTE_FOR_SCROLLING]: image.id })}
              >
                <div
                  className={classNames(styles.imageContainer, {
                    [styles.isUploading]: !isUploadedCarouselItem(image),
                  })}
                  style={itemWidth ? { width: itemWidth } : undefined}
                >
                  {isUploadedCarouselItem(imageForDisplay) ||
                  !!imageForDisplay.uploadPreviewSource ? (
                    <ImageNode
                      className={classNames(styles.image, {
                        // We want the image to cover the container, but also don't want to stretch the image.
                        // So we only apply the cover style if the image is taller than the container.
                        [styles.shouldCover]:
                          image.natural?.height &&
                          image.natural.height > containerHeight,
                      })}
                      onLoad={() => performScrolling({ behavior: 'instant' })}
                      image={imageForDisplay}
                      fullscreen={fullscreen}
                    />
                  ) : (
                    <div className={styles.placeholder} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {showControls && (
          <>
            <div className={styles.buttonPrevious}>
              <CarouselSliderButton
                selectedIndex={selectedIndex}
                onSelectedIndexChange={onSelectedIndexChange}
                imageCount={images.length}
                direction="previous"
              />
            </div>
            <div className={styles.buttonNext}>
              <CarouselSliderButton
                selectedIndex={selectedIndex}
                onSelectedIndexChange={onSelectedIndexChange}
                imageCount={images.length}
                direction="next"
              />
            </div>
          </>
        )}
      </div>
    </Slot>
  );
};

export default React.memo(CarouselSlider);
